import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { fromUnixTime, differenceInMinutes, addMinutes } from "date-fns";
import { Slot } from "core/types";
import { useSelectedPsychologist } from "components/booking/hooks/useSelectedPsychologist";
import { useHandleDropPickerUpdate } from "components/booking/hooks/useHandleDropPickerUpdate";
import { useIsSelectedMarketGB } from "core/selectors/marketSelectors";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { useAuth } from "@mindlercare/auth-ui";
import { invalidateBookings } from "components/patients/patientCard/bookings/useBookings";

export const useSlotDateTimePicker = ({ slot }: { slot: Slot }) => {
  const [time, setTime] = useState(fromUnixTime(slot.startsAt));
  const isMarketGB = useIsSelectedMarketGB();

  const invalidate = invalidateBookings();

  const { t } = useTranslation();

  const api = useMindlerApi();
  const { mutateAsync: moveSlot } = useMutation(
    api.slots.psychologist.move.mutationOptions(),
  );

  const selectedPsychologist = useSelectedPsychologist();
  const { userId } = useAuth();
  const psychologistUserId = (selectedPsychologist?.user.userId ??
    userId) as number;

  const onDropPickerUpdate = useHandleDropPickerUpdate();

  const handleTimeUpdate = useCallback(
    async (newStartTime: Date | null) => {
      if (!newStartTime) return;
      newStartTime.setSeconds(0, 0);
      const slotLength = differenceInMinutes(
        fromUnixTime(slot.endsAt),
        fromUnixTime(slot.startsAt),
      );

      await onDropPickerUpdate(
        {
          event: slot,
          start: newStartTime,
          end: addMinutes(newStartTime, slotLength),
          startDate: newStartTime,
          endDate: addMinutes(newStartTime, slotLength),
        },
        t,
        psychologistUserId,
        moveSlot,
      );
      if (window?.location?.pathname?.includes("bookings")) {
        await invalidate(slot.patientId);
      }
    },
    [slot, t, psychologistUserId, moveSlot, invalidate, onDropPickerUpdate],
  );

  const handleTimeChange = useCallback((pickerTime: Date | null) => {
    if (!pickerTime) return;
    return setTime(pickerTime);
  }, []);

  return { time, handleTimeUpdate, handleTimeChange, isMarketGB };
};
